import React, { useEffect, useState } from "react";
import { Download, Loader2, RefreshCw, Trash2, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { AdminHeader, Badge, Field, Panel, Row, Table, inputCls } from "../ui";

const BLANK = { email: "", name: "", phone: "", vehicle: "", tags: "" };

export default function ContactsAdmin() {
  const [data, setData] = useState(null);
  const [segments, setSegments] = useState({ tags: [], sources: [] });
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("");
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(BLANK);
  const [paste, setPaste] = useState("");
  const [inspect, setInspect] = useState(null);
  const [importTags, setImportTags] = useState("");
  const [importSource, setImportSource] = useState("imported list");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      adminApi.get("/admin/marketing/contacts", { params: { q: q || undefined, tag: tag || undefined, limit: 200 } }),
      adminApi.get("/admin/marketing/segments"),
    ])
      .then(([c, s]) => {
        setData(c.data);
        setSegments(s.data);
      })
      .catch(() => toast.error("Could not load contacts"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [q, tag]);

  const addOne = async () => {
    setBusy(true);
    try {
      await adminApi.post("/admin/marketing/contacts", {
        ...adding,
        tags: adding.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      setAdding(BLANK);
      toast.success("Contact saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save that");
    } finally {
      setBusy(false);
    }
  };

  const syncSite = async () => {
    setBusy(true);
    try {
      const r = await adminApi.post("/admin/marketing/contacts/sync-site");
      toast.success(`${r.data.added} new, ${r.data.updated} updated`, {
        description: "Pulled from website enquiries and paid orders.",
      });
      load();
    } catch {
      toast.error("Could not sync");
    } finally {
      setBusy(false);
    }
  };

  const doInspect = async () => {
    setBusy(true);
    try {
      const r = await adminApi.post("/admin/marketing/contacts/inspect", { text: paste });
      setInspect(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read that");
    } finally {
      setBusy(false);
    }
  };

  const doImport = async () => {
    setBusy(true);
    try {
      const r = await adminApi.post("/admin/marketing/contacts/import", {
        text: paste,
        mapping: inspect.mapping,
        tags: importTags.split(",").map((t) => t.trim()).filter(Boolean),
        source: importSource,
      });
      toast.success(`${r.data.added} added, ${r.data.updated} updated`, {
        description: r.data.skipped_count ? `${r.data.skipped_count} rows had no valid email` : undefined,
      });
      setPaste("");
      setInspect(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleSub = async (c) => {
    await adminApi.patch(`/admin/marketing/contacts/${c.id}`, { subscribed: !c.subscribed });
    load();
  };

  const remove = async (c) => {
    await adminApi.delete(`/admin/marketing/contacts/${c.id}`);
    toast.success("Removed");
    load();
  };

  return (
    <div className="space-y-6" data-testid="contacts-admin">
      <AdminHeader title="Contacts" subtitle="Who your mailouts go to, and where each person came from">
        <button onClick={syncSite} disabled={busy} className="btn-ghost inline-flex items-center gap-2" data-testid="contacts-sync">
          <Download className="h-4 w-4" /> Pull from site
        </button>
        <button onClick={load} className="btn-ghost inline-flex items-center gap-2">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </AdminHeader>

      <div className="grid gap-6 lg:grid-cols-2">
        <Panel>
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider gold-text">
            <UserPlus className="h-4 w-4" /> Add one by hand
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Field label="Email">
              <input value={adding.email} onChange={(e) => setAdding({ ...adding, email: e.target.value })} className={inputCls} data-testid="contact-email-input" />
            </Field>
            <Field label="Name">
              <input value={adding.name} onChange={(e) => setAdding({ ...adding, name: e.target.value })} className={inputCls} data-testid="contact-name-input" />
            </Field>
            <Field label="Phone">
              <input value={adding.phone} onChange={(e) => setAdding({ ...adding, phone: e.target.value })} className={inputCls} />
            </Field>
            <Field label="Vehicle">
              <input value={adding.vehicle} onChange={(e) => setAdding({ ...adding, vehicle: e.target.value })} className={inputCls} />
            </Field>
          </div>
          <div className="mt-3">
            <Field label="Tags" hint="Comma separated - e.g. trade, gvm, american trucks">
              <input value={adding.tags} onChange={(e) => setAdding({ ...adding, tags: e.target.value })} className={inputCls} data-testid="contact-tags-input" />
            </Field>
          </div>
          <button onClick={addOne} disabled={busy || !adding.email} className="btn-gold mt-4 px-4 py-2 text-xs disabled:opacity-50" data-testid="contact-add-button">
            Save contact
          </button>
        </Panel>

        <Panel>
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider gold-text">
            <Users className="h-4 w-4" /> Paste a list from a spreadsheet
          </p>
          <p className="mt-2 text-xs leading-relaxed text-[color:var(--nq-stone)]">
            Copy the rows straight out of Excel (include the header row) and paste them here. I'll
            show you which column is which before anything is saved.
          </p>
          <textarea
            value={paste}
            onChange={(e) => { setPaste(e.target.value); setInspect(null); }}
            rows={5}
            placeholder={"Name\tEmail\tPhone\tType\nJoe Bloggs\tjoe@example.com\t0400 000 000\ttrade"}
            className={`${inputCls} mt-3 font-mono text-xs`}
            data-testid="contacts-paste-area"
          />
          {!inspect ? (
            <button onClick={doInspect} disabled={busy || !paste.trim()} className="btn-outline-nq mt-3 px-4 py-2 text-xs disabled:opacity-50" data-testid="contacts-inspect-button">
              Check the columns
            </button>
          ) : (
            <div className="mt-3 space-y-3">
              <p className="text-xs text-[color:var(--nq-stone)]" data-testid="contacts-mapping-summary">
                {inspect.row_count} rows. Email column: <strong>{inspect.mapping.email || "not found"}</strong>
                {inspect.mapping.tags ? `, tags from "${inspect.mapping.tags}"` : ""}
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Email column">
                  <select value={inspect.mapping.email || ""} onChange={(e) => setInspect({ ...inspect, mapping: { ...inspect.mapping, email: e.target.value } })} className={inputCls} data-testid="contacts-email-column">
                    <option value="" style={{ color: "#111" }}>-- choose --</option>
                    {inspect.headers.map((h) => (
                      <option key={h} value={h} style={{ color: "#111" }}>{h}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Tags column" hint="Optional - the column that says what kind of customer they are">
                  <select value={inspect.mapping.tags || ""} onChange={(e) => setInspect({ ...inspect, mapping: { ...inspect.mapping, tags: e.target.value } })} className={inputCls}>
                    <option value="" style={{ color: "#111" }}>-- none --</option>
                    {inspect.headers.map((h) => (
                      <option key={h} value={h} style={{ color: "#111" }}>{h}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Tag every row with" hint="Comma separated, applied on top of the tags column">
                  <input value={importTags} onChange={(e) => setImportTags(e.target.value)} className={inputCls} data-testid="contacts-import-tags" />
                </Field>
                <Field label="Where this list came from">
                  <input value={importSource} onChange={(e) => setImportSource(e.target.value)} className={inputCls} />
                </Field>
              </div>
              <button onClick={doImport} disabled={busy || !inspect.mapping.email} className="btn-gold px-4 py-2 text-xs disabled:opacity-50" data-testid="contacts-import-button">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Import ${inspect.row_count} rows`}
              </button>
            </div>
          )}
        </Panel>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone or vehicle" className={`${inputCls} max-w-sm`} data-testid="contacts-search" />
        <button onClick={() => setTag("")} className={`rounded-lg px-3 py-1.5 text-xs ${tag === "" ? "gold-text" : "text-[color:var(--nq-stone)]"}`}>
          All ({data?.total ?? 0})
        </button>
        {segments.tags.map((t) => (
          <button
            key={t.name}
            onClick={() => setTag(t.name)}
            className={`rounded-lg px-3 py-1.5 text-xs ${tag === t.name ? "gold-text" : "text-[color:var(--nq-stone)]"}`}
            data-testid={`contacts-tag-${t.name}`}
          >
            {t.name} ({t.count})
          </button>
        ))}
      </div>

      {loading ? (
        <Panel><p className="flex items-center gap-2 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</p></Panel>
      ) : (data?.contacts || []).length === 0 ? (
        <Panel><p className="text-sm text-[color:var(--nq-stone)]">No contacts yet. Pull from the site or paste a list above.</p></Panel>
      ) : (
        <Table head={["Contact", "Tags", "Came from", "Mailouts", ""]}>
          {data.contacts.map((c) => (
            <Row key={c.id} testid="contact-row">
              <td className="p-3">
                <p className="font-medium">{c.name || c.email}</p>
                <p className="text-xs text-[color:var(--nq-stone)]">{c.email}{c.phone ? ` · ${c.phone}` : ""}</p>
              </td>
              <td className="p-3">
                <span className="flex flex-wrap gap-1">
                  {(c.tags || []).map((t) => <Badge key={t} tone="gold">{t}</Badge>)}
                </span>
              </td>
              <td className="p-3 text-xs text-[color:var(--nq-stone)]">{c.source}</td>
              <td className="p-3">
                <button onClick={() => toggleSub(c)} data-testid={`contact-toggle-${c.id}`}>
                  {c.subscribed ? <Badge tone="green">subscribed</Badge> : <Badge tone="red">unsubscribed</Badge>}
                </button>
              </td>
              <td className="p-3 text-right">
                <button onClick={() => remove(c)} className="text-[color:var(--nq-stone)] hover:text-red-300" data-testid={`contact-delete-${c.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </td>
            </Row>
          ))}
        </Table>
      )}
    </div>
  );
}
